import { darken, lighten } from "@mui/material";
import { capitalDesignTokens } from "@opengov/capital-design-tokens";
const DEFAULT_CHIP_LABEL_PADDING = 1;
const FOREGROUND_COLOR_NUM = "700";
const BACKGROUND_STRONG_COLOR_NUM = "600";
const BACKGROUND_LIGHT_COLOR_NUM = "100";
const getTextColor = (isStrong, color) => isStrong ? capitalDesignTokens.foundations.colors.white : color;
const getColorToken = (colorName, strength) => capitalDesignTokens.foundations.colors[`${colorName}${strength}`];
const chipColors = {
  // Note: default colors are resolved dynamically via getChipColors(theme) to be theme-aware.
  // These fallback values are only used when theme is not provided (external callers).
  default: {
    color: (isStrong) => getTextColor(isStrong, capitalDesignTokens.foundations.colors.gray800),
    background: (isStrong) => isStrong ? capitalDesignTokens.foundations.colors.gray700 : capitalDesignTokens.foundations.colors.gray100,
    border: capitalDesignTokens.foundations.colors.gray400
  },
  success: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("green", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("green", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("green", FOREGROUND_COLOR_NUM)
  },
  error: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("red", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("red", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("red", FOREGROUND_COLOR_NUM)
  },
  warning: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("yellow", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("yellow", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("yellow", FOREGROUND_COLOR_NUM)
  },
  inProgress: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("purple", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("purple", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("purple", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.inProgress
  },
  indigo: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("indigo", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("indigo", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("indigo", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.indigo
  },
  periwinkle: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("periwinkle", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("periwinkle", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("periwinkle", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.periwinkle
  },
  jade: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("jade", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("jade", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("jade", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.jade
  },
  port: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("port", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("port", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("port", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.port
  },
  rose: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("rose", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("rose", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("rose", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.rose
  },
  magenta: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("magenta", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("magenta", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("magenta", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.magenta
  },
  orange: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("orange", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("orange", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("orange", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.orange
  },
  terracotta: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("terracotta", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("terracotta", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("terracotta", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.terracotta
  },
  blue: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("blue", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("blue", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("blue", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.blue
  },
  cerulean: {
    color: (isStrong) => getTextColor(isStrong, getColorToken("cerulean", FOREGROUND_COLOR_NUM)),
    background: (isStrong) => getColorToken("cerulean", isStrong ? BACKGROUND_STRONG_COLOR_NUM : BACKGROUND_LIGHT_COLOR_NUM),
    border: getColorToken("cerulean", FOREGROUND_COLOR_NUM),
    paletteFamily: (palette) => palette.cerulean
  }
};
const isChipColor = (value) => Object.prototype.hasOwnProperty.call(chipColors, value);
const getChipColors = (ownerStateColor, ownerStateVariant, theme) => {
  var _a;
  const isStrong = ownerStateVariant === "strong";
  const isMinimal = ownerStateVariant === "minimal";
  const color = ownerStateColor === "secondary" || ownerStateColor === "info" ? "default" : ownerStateColor;
  if ((color === "default" || !isChipColor(color)) && theme) {
    const textColor = isStrong ? theme.palette.common.white : theme.palette.text.secondary;
    const bgColor = isStrong ? theme.palette.action.disabled : theme.palette.action.hover;
    return {
      color: textColor,
      backgroundColor: isMinimal ? "transparent" : bgColor,
      borderColor: theme.palette.divider
    };
  }
  const colorConfig = isChipColor(color) ? chipColors[color] : chipColors.default;
  const family = (theme == null ? void 0 : theme.palette.mode) === "light" ? (_a = colorConfig.paletteFamily) == null ? void 0 : _a.call(colorConfig, theme.palette) : void 0;
  if ((family == null ? void 0 : family.main) && family.dark && family.light && family.contrastText) {
    return {
      color: isStrong ? family.contrastText : family.dark,
      backgroundColor: isMinimal ? "transparent" : isStrong ? family.main : family.light,
      borderColor: family.dark
    };
  }
  return {
    color: colorConfig.color(isStrong),
    backgroundColor: isMinimal ? "transparent" : colorConfig.background(isStrong),
    borderColor: colorConfig.border
  };
};
const getChipSizeStyles = (theme) => {
  return {
    // Extra small size is exclusively used for Autocomplete selection tags
    extraSmall: {
      fontSize: capitalDesignTokens.foundations.typography.fontSize.bodyXSmall,
      height: "20px"
    },
    small: {
      fontSize: capitalDesignTokens.componentFontSizes.small,
      height: `${capitalDesignTokens.componentSizes.small}px`,
      padding: theme.spacing(0, 0.5)
    },
    medium: {
      fontSize: capitalDesignTokens.componentFontSizes.medium,
      height: `${capitalDesignTokens.componentSizes.medium}px`,
      padding: theme.spacing(0, 0.5)
    },
    large: {
      fontSize: capitalDesignTokens.componentFontSizes.large,
      height: `${capitalDesignTokens.componentSizes.large}px`,
      padding: theme.spacing(0, 0.75)
    }
  };
};
const getChipRootStyles = (colors) => {
  return {
    cursor: "default",
    alignItems: "center",
    fontWeight: capitalDesignTokens.foundations.typography.fontWeight.medium,
    borderRadius: "4px",
    color: colors.color,
    backgroundColor: colors.backgroundColor
  };
};
const MuiChip = {
  styleOverrides: {
    root: ({ theme, ownerState }) => {
      const chipSizeStyles = getChipSizeStyles(theme);
      const colors = getChipColors(ownerState.color || "default", ownerState.variant, theme);
      const chipRootStyles = getChipRootStyles(colors);
      return {
        ...chipRootStyles,
        display: "inline-flex",
        ...ownerState.size === "small" && chipSizeStyles.small,
        ...ownerState.size === "medium" && chipSizeStyles.medium,
        ...ownerState.size === "large" && chipSizeStyles.large,
        ...ownerState.variant === "minimal" && {
          padding: 0
        },
        "&.Mui-focusVisible, &.MuiChip-clickable:hover, &:focus-visible": {
          ...ownerState.variant !== "minimal" && ownerState.variant !== "strong" && {
            backgroundColor: darken(colors.backgroundColor, 0.06)
          },
          ...ownerState.variant === "minimal" && {
            color: lighten(colors.color, 0.2),
            backgroundColor: colors.backgroundColor
          },
          ...ownerState.variant === "strong" && {
            backgroundColor: darken(colors.backgroundColor, 0.25)
          }
        },
        "&.Mui-disabled": {
          opacity: 0.6
        }
      };
    },
    outlined: ({ ownerState, theme }) => {
      const colors = getChipColors(ownerState.color || "default", ownerState.variant, theme);
      return {
        borderColor: colors.borderColor,
        "&.Mui-focusVisible, &:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2
        }
      };
    },
    label: ({ theme, ownerState }) => {
      const hasLeadingElement = ownerState.avatar || ownerState.icon;
      const hasTrailingElement = ownerState.deleteIcon;
      const isMinimal = ownerState.variant === "minimal";
      return {
        paddingLeft: theme.spacing(hasLeadingElement ? 1.25 : isMinimal ? 0 : DEFAULT_CHIP_LABEL_PADDING),
        paddingRight: theme.spacing(hasTrailingElement ? 1.25 : isMinimal ? 0 : DEFAULT_CHIP_LABEL_PADDING)
      };
    },
    icon: {
      fontSize: "inherit",
      color: "inherit"
    },
    deleteIcon: ({ ownerState, theme }) => {
      const colors = getChipColors(ownerState.color || "default", ownerState.variant, theme);
      return {
        fontSize: "inherit",
        color: "inherit",
        "&:hover": {
          color: darken(colors.color, 0.3)
        }
      };
    },
    clickable: {
      "&:hover": {
        cursor: "pointer"
      }
    }
  }
};
export {
  DEFAULT_CHIP_LABEL_PADDING,
  MuiChip,
  getChipColors,
  getChipRootStyles,
  getChipSizeStyles
};
//# sourceMappingURL=MuiChip.js.map
